package com.dualremotetest.web

import com.dualremotetest.Keys
import com.dualremotetest.Phase
import com.dualremotetest.TankMatch
import com.dualremotetest.directionDelta
import com.dualremotetest.touchPadDirection
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.CENTER
import org.w3c.dom.CanvasTextAlign
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import kotlin.math.PI
import kotlin.math.max
import kotlin.math.min

/**
 * The on-screen controls, as on Android phones and tablets (TankUi.TouchControls), drawn on a canvas over the game: a
 * D-pad in the left strip beside the picture (a touch anywhere in the strip counts, measured from the pad's centre),
 * FIRE in the right strip with a pause/back button above it. They send the same key events a remote does, so they
 * drive the menus too. The strips stay clear of the notch and the Home indicator (the safe-area insets).
 */
class TouchControls(
    private val canvas: HTMLCanvasElement,
    private val match: TankMatch,
    private val onKey: (keyCode: Int, down: Boolean) -> Unit,
    /** A tap on the picture between the strips, in CSS px (sent when the finger lifts, which lets a page copy text). */
    private val onPictureTap: (clientX: Double, clientY: Double) -> Unit = { _, _ -> },
) {
    private val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
    private var w = 0.0
    private var h = 0.0
    private var dpr = 1.0
    /** Width of each side strip in CSS px: the room beside the picture, at least [RESERVE_CSS_PX]. */
    private var strip = 0.0
    private var insetLeft = 0.0
    private var insetRight = 0.0

    private enum class Role { PAD, FIRE, PAUSE, PICTURE }
    private val pointers = HashMap<Int, Role>()
    private var padPointer: Int? = null
    private var held: Int? = null // the D-pad direction held
    private var firing = false

    init {
        canvas.style.display = "block"
        canvas.addEventListener("pointerdown", { onPointer(it, "down") })
        canvas.addEventListener("pointermove", { onPointer(it, "move") })
        canvas.addEventListener("pointerup", { onPointer(it, "up") })
        canvas.addEventListener("pointercancel", { onPointer(it, "cancel") })
        // No scrolling, pinch zoom or callout menus from presses on the controls.
        for (type in listOf("touchstart", "touchmove", "touchend", "gesturestart", "contextmenu")) {
            canvas.addEventListener(type, { it.preventDefault() }, js("({ passive: false })"))
        }
    }

    fun resize(width: Int, height: Int, ratio: Double, pictureCssWidth: Double) {
        w = width.toDouble()
        h = height.toDouble()
        dpr = ratio
        canvas.width = (w * dpr).toInt()
        canvas.height = (h * dpr).toInt()
        canvas.style.width = "${w}px"
        canvas.style.height = "${h}px"
        strip = max((w - pictureCssWidth) / 2, RESERVE_CSS_PX.toDouble())
        val probe = document.getElementById("safe-area") as HTMLElement?
        val style = probe?.let { window.getComputedStyle(it) }
        insetLeft = style?.paddingLeft?.removeSuffix("px")?.toDoubleOrNull() ?: 0.0
        insetRight = style?.paddingRight?.removeSuffix("px")?.toDoubleOrNull() ?: 0.0
    }

    /** Lets go of everything (the page lost focus); the game clears its own held state too. */
    fun releaseAll() {
        held?.let { onKey(it, false) }
        if (firing) onKey(Keys.KEYCODE_DPAD_CENTER, false)
        held = null
        firing = false
        padPointer = null
        pointers.clear()
    }

    // ---------------------------------------------------------------- geometry (CSS px)

    private val pauseH get() = h / 4
    private fun leftStrip() = insetLeft to strip
    private fun rightStrip() = (w - strip) to (w - insetRight)

    /** The D-pad's and FIRE's circles: the same size at the same height, centred in their strip (TankUi.padGeometry). */
    private fun circle(x0: Double, x1: Double): Triple<Double, Double, Double> {
        val below = h - pauseH
        val sw = max(x1 - x0, 1.0)
        return Triple(x0 + sw / 2, pauseH + below * 0.55, min(sw * 0.34, below * 0.26))
    }

    private fun padCircle() = leftStrip().let { (a, b) -> circle(a, b) }
    private fun fireCircle() = rightStrip().let { (a, b) -> circle(a, b) }

    // ---------------------------------------------------------------- input

    private fun onPointer(e: Event, kind: String) {
        val ev = e.asDynamic()
        val id = ev.pointerId as Int
        val x = ev.clientX as Double
        val y = ev.clientY as Double
        e.preventDefault()
        when (kind) {
            "down" -> {
                val role = when {
                    x <= strip -> Role.PAD
                    x >= w - strip -> if (y < pauseH) Role.PAUSE else Role.FIRE
                    else -> Role.PICTURE
                }
                runCatching { canvas.asDynamic().setPointerCapture(id) }
                pointers[id] = role
                when (role) {
                    Role.PAD -> if (padPointer == null) {
                        padPointer = id
                        steer(x, y)
                    }
                    Role.FIRE -> if (!firing) {
                        firing = true
                        onKey(Keys.KEYCODE_DPAD_CENTER, true)
                    }
                    Role.PAUSE, Role.PICTURE -> Unit
                }
            }
            "move" -> if (id == padPointer) steer(x, y)
            "up", "cancel" -> {
                val role = pointers.remove(id) ?: return
                when (role) {
                    Role.PAD -> if (id == padPointer) {
                        padPointer = null
                        held?.let { onKey(it, false) }
                        held = null
                    }
                    Role.FIRE -> if (firing && pointers.values.none { it == Role.FIRE }) {
                        firing = false
                        onKey(Keys.KEYCODE_DPAD_CENTER, false)
                    }
                    // Pause during play, back in the other screens; nothing on the title, where BACK would exit.
                    Role.PAUSE -> if (kind == "up" && match.phase != Phase.TITLE) {
                        onKey(Keys.KEYCODE_BACK, true)
                        onKey(Keys.KEYCODE_BACK, false)
                    }
                    Role.PICTURE -> if (kind == "up") onPictureTap(x, y)
                }
            }
        }
    }

    private fun steer(x: Double, y: Double) {
        val (cx, cy, r) = padCircle()
        val want = touchPadDirection((x - cx).toFloat(), (y - cy).toFloat(), r.toFloat())
        if (want == held) return
        held?.let { onKey(it, false) }
        want?.let { onKey(it, true) }
        held = want
    }

    // ---------------------------------------------------------------- drawing

    fun draw() {
        ctx.setTransform(1.0, 0.0, 0.0, 1.0, 0.0, 0.0)
        ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
        ctx.setTransform(dpr, 0.0, 0.0, dpr, 0.0, 0.0)

        val (px, py, pr) = padCircle()
        ring(px, py, pr, FILL)
        for (dir in listOf(Keys.KEYCODE_DPAD_UP, Keys.KEYCODE_DPAD_DOWN, Keys.KEYCODE_DPAD_LEFT, Keys.KEYCODE_DPAD_RIGHT)) {
            arrow(px, py, pr, dir, if (dir == held) "#ffffff" else INK)
        }

        val (fx, fy, fr) = fireCircle()
        ring(fx, fy, fr, if (firing) LIT else FILL)
        ctx.fillStyle = INK
        ctx.font = "bold ${fr * 0.36}px -apple-system, system-ui, sans-serif"
        ctx.textAlign = CanvasTextAlign.CENTER
        ctx.fillText("FIRE", fx, fy + fr * 0.36 * 0.35)

        if (match.phase == Phase.TITLE) return
        val (a, b) = rightStrip()
        val cx = (a + b) / 2
        val cy = pauseH / 2
        val r = min((b - a) * 0.18, pauseH * 0.3)
        ring(cx, cy, r, FILL)
        ctx.fillStyle = INK
        if (match.phase == Phase.PLAYING) {
            val bw = r * 0.22
            val bh = r * 0.9
            ctx.fillRect(cx - bw * 1.6, cy - bh / 2, bw, bh)
            ctx.fillRect(cx + bw * 0.6, cy - bh / 2, bw, bh)
        } else {
            // A "back" triangle with its middle on the circle's centre (nudged toward the tip so it looks centred).
            val tw = r * 0.8
            val half = r * 0.45
            val left = cx - tw / 2 - tw / 12
            ctx.beginPath()
            ctx.moveTo(left, cy)
            ctx.lineTo(left + tw, cy - half)
            ctx.lineTo(left + tw, cy + half)
            ctx.closePath()
            ctx.fill()
        }
    }

    private fun ring(x: Double, y: Double, r: Double, fill: String) {
        ctx.beginPath()
        ctx.arc(x, y, r, 0.0, 2 * PI)
        ctx.fillStyle = fill
        ctx.fill()
        ctx.lineWidth = 2.0
        ctx.strokeStyle = INK
        ctx.stroke()
    }

    /** A triangle pointing in [dir], placed toward that edge of the circle (TankUi.arrow). */
    private fun arrow(cx: Double, cy: Double, r: Double, dir: Int, color: String) {
        val (dx, dy) = directionDelta(dir)
        val tipX = cx + dx * r * 0.85
        val tipY = cy + dy * r * 0.85
        val baseX = cx + dx * r * 0.5
        val baseY = cy + dy * r * 0.5
        val half = r * 0.22
        ctx.beginPath()
        ctx.moveTo(tipX, tipY)
        ctx.lineTo(baseX + dy * half, baseY + dx * half)
        ctx.lineTo(baseX - dy * half, baseY - dx * half)
        ctx.closePath()
        ctx.fillStyle = color
        ctx.fill()
    }

    companion object {
        /** Room kept on each side of the picture for the controls, as TOUCH_RESERVE (150 dp) on Android. */
        const val RESERVE_CSS_PX = 150
        private const val INK = "rgba(255,255,255,0.6)"
        private const val FILL = "rgba(255,255,255,0.13)"
        private const val LIT = "rgba(255,255,255,0.4)"
    }
}
